import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  KeyRound,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldOff,
  ShieldCheck,
  Smartphone,
  Trash2,
  UserPlus,
  Users,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { GAUTO_API } from '@/lib/gauto-endpoints'
import {
  EXPIRING_SOON_DAYS,
  GautoApiError,
  addTerm,
  buildLicenceWrite,
  clampMaxDevices,
  daysUntil,
  describeError,
  extendExpiry,
  formatDate,
  formatDateTime,
  formatRelative,
  gautoRequest,
  licenceState,
  shortDeviceId,
  type ExpiryUnit,
  type GautoLicenceDetail,
  type GautoLicenceListRow,
  type GautoLicencePayload,
  type GautoSummary,
  type LicenceState,
  type LicenceWriteChanges,
} from '@/lib/gauto'

const EXTEND_OPTIONS: { days: number; label: string }[] = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 180, label: '180 days' },
  { days: 365, label: '1 year' },
]

const STATE_STYLES: Record<LicenceState, { label: string; className: string }> = {
  active: { label: 'Active', className: 'bg-green-100 text-green-800' },
  expiring: { label: 'Expiring', className: 'bg-amber-100 text-amber-800' },
  expired: { label: 'Expired', className: 'bg-gray-200 text-gray-700' },
  revoked: { label: 'Blocked', className: 'bg-red-100 text-red-800' },
}

/* ---------------------------------------------------------------- primitives */

function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  tone = 'indigo',
}: {
  label: string
  value: string | number
  sub?: string
  icon: typeof Users
  tone?: 'indigo' | 'green' | 'amber' | 'red' | 'gray'
}) {
  const tones: Record<string, string> = {
    indigo: 'bg-indigo-50 text-indigo-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    gray: 'bg-gray-100 text-gray-600',
  }

  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className={cn('flex-shrink-0 rounded-md p-3', tones[tone])}>
            <Icon className="h-6 w-6" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
              <dd className="text-2xl font-semibold text-gray-900">{value}</dd>
              {sub && <dd className="text-xs text-gray-400 mt-0.5">{sub}</dd>}
            </dl>
          </div>
        </div>
      </div>
    </div>
  )
}

function Card({
  title,
  subtitle,
  action,
  children,
}: {
  title: string
  subtitle?: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-4 sm:px-6 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-medium text-gray-900">{title}</h3>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  )
}

function StatusBadge({ row, now }: { row: { status: string; expires_at: number }; now: number }) {
  const state = licenceState(row, now)
  const style = STATE_STYLES[state]
  return (
    <span
      className={cn(
        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
        style.className,
      )}
    >
      {style.label}
    </span>
  )
}

function Drawer({ title, onClose, children }: { title: ReactNode; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-gray-900/40" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-xl bg-white shadow-xl h-full overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0">{title}</div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-6">{children}</div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</label>
      {children}
    </div>
  )
}

const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none'

function Notice({ tone, children }: { tone: 'error' | 'info'; children: ReactNode }) {
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md px-3 py-2 text-sm',
        tone === 'error' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700',
      )}
    >
      {tone === 'error' ? (
        <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
      ) : (
        <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
      )}
      <div className="min-w-0">{children}</div>
    </div>
  )
}

/* ------------------------------------------------------------------ the page */

export default function GautoPage() {
  const [summary, setSummary] = useState<GautoSummary | null>(null)
  const [licences, setLicences] = useState<GautoLicenceListRow[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<'expiry' | 'email' | 'devices' | 'status'>('expiry')
  const [busyEmail, setBusyEmail] = useState<string | null>(null)
  const [actionError, setActionError] = useState<{ email: string; message: string } | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const [inviteOpen, setInviteOpen] = useState(false)
  const [detailEmail, setDetailEmail] = useState<string | null>(null)

  // A single clock for the render keeps every badge and "days left" consistent.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else setLoading(true)
    try {
      const [summaryResult, listResult] = await Promise.all([
        gautoRequest<GautoSummary>(GAUTO_API.summary),
        gautoRequest<{ licenses: GautoLicenceListRow[] }>(GAUTO_API.licenses),
      ])
      setSummary(summaryResult)
      setLicences(listResult.licenses ?? [])
      setLastUpdated(new Date())
      setLoadError(null)
    } catch (error) {
      setLoadError(describeError(error))
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const openDetail = useCallback((email: string) => setDetailEmail(email), [])

  /** Run a write, surface its error against the row it belongs to, then reload. */
  const runAction = useCallback(
    async (email: string, action: () => Promise<string | null>) => {
      setBusyEmail(email)
      setActionError(null)
      setNotice(null)
      try {
        const message = await action()
        await load(true)
        if (message) setNotice(message)
      } catch (error) {
        setActionError({ email, message: describeError(error) })
      } finally {
        setBusyEmail(null)
      }
    },
    [load],
  )

  const extend = useCallback(
    (licence: GautoLicenceListRow, days: number) =>
      runAction(licence.email, async () => {
        const detail = await gautoRequest<GautoLicenceDetail>(
          `${GAUTO_API.license}?email=${encodeURIComponent(licence.email)}`,
        )
        const expiresAt = extendExpiry(detail.licence.expires_at, days, 'days')
        await gautoRequest(GAUTO_API.license, {
          method: 'POST',
          body: buildLicenceWrite(detail.licence, { expiresAt }),
        })
        return `${licence.email} extended to ${formatDate(expiresAt)}.`
      }),
    [runAction],
  )

  const setBlocked = useCallback(
    (licence: GautoLicenceListRow, blocked: boolean) =>
      runAction(licence.email, async () => {
        const detail = await gautoRequest<GautoLicenceDetail>(
          `${GAUTO_API.license}?email=${encodeURIComponent(licence.email)}`,
        )
        await gautoRequest(GAUTO_API.license, {
          method: 'POST',
          body: buildLicenceWrite(detail.licence, { status: blocked ? 'revoked' : 'active' }),
        })
        return blocked
          ? `${licence.email} blocked. Renewals stop immediately; the phone keeps working until its 30-day token lapses.`
          : `${licence.email} unblocked and can renew again.`
      }),
    [runAction],
  )

  const resetDevices = useCallback(
    (email: string) =>
      runAction(email, async () => {
        const result = await gautoRequest<{ removed: number }>(GAUTO_API['devices/reset'], {
          method: 'POST',
          body: { email },
        })
        return `${result.removed} device slot${result.removed === 1 ? '' : 's'} freed for ${email}. The app on that phone keeps working until its token lapses.`
      }),
    [runAction],
  )

  const removeLicence = useCallback(
    (licence: GautoLicenceListRow) => {
      if (licence.email === detailEmail) setDetailEmail(null)
      return runAction(licence.email, async () => {
        const result = await gautoRequest<{ removedDevices: number; removedLicence: number }>(
          GAUTO_API['license/remove'],
          { method: 'POST', body: { email: licence.email } },
        )
        const parts = [
          `${result.removedLicence} licence${result.removedLicence === 1 ? '' : 's'}`,
          `${result.removedDevices} device${result.removedDevices === 1 ? '' : 's'}`,
        ]
        return `Removed ${parts.join(' and ')} for ${licence.email}.`
      })
    },
    [detailEmail, runAction],
  )

  const confirmRemove = useCallback(
    (licence: GautoLicenceListRow) => {
      const confirmed = window.confirm(
        `Remove ${licence.email}?\n\nThis permanently deletes the licence AND its ${licence.devices} device record${
          licence.devices === 1 ? '' : 's'
        }. It cannot be undone.`,
      )
      if (confirmed) removeLicence(licence)
    },
    [removeLicence],
  )

  const rename = useCallback(
    (from: string, to: string) =>
      runAction(from, async () => {
        await gautoRequest(GAUTO_API['license/rename'], { method: 'POST', body: { from, to } })
        setDetailEmail(to)
        return `${from} renamed to ${to}. Devices and expiry moved with it.`
      }),
    [runAction],
  )

  const invite = useCallback(
    async (payload: GautoLicencePayload) => {
      setBusyEmail(payload.email)
      setActionError(null)
      try {
        await gautoRequest(GAUTO_API.license, { method: 'POST', body: payload })
        await load(true)
        setInviteOpen(false)
        setNotice(`Invited ${payload.email}.`)
      } catch (error) {
        setActionError({ email: payload.email, message: describeError(error) })
      } finally {
        setBusyEmail(null)
      }
    },
    [load],
  )

  const removeDevice = useCallback(
    async (deviceId: string, email: string) => {
      setBusyEmail(email)
      setActionError(null)
      try {
        await gautoRequest(GAUTO_API['device/remove'], { method: 'POST', body: { deviceId } })
        await load(true)
        setNotice(`Device ${shortDeviceId(deviceId, 8)} freed for ${email}.`)
      } catch (error) {
        setActionError({ email, message: describeError(error) })
      } finally {
        setBusyEmail(null)
      }
    },
    [load],
  )

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const filtered = needle
      ? licences.filter(
          (row) =>
            row.email.toLowerCase().includes(needle) ||
            (row.note ?? '').toLowerCase().includes(needle),
        )
      : licences

    const rank: Record<LicenceState, number> = { expired: 0, revoked: 1, expiring: 2, active: 3 }

    return [...filtered].sort((a, b) => {
      switch (sort) {
        case 'email':
          return a.email.localeCompare(b.email)
        case 'devices':
          return b.devices - a.devices || a.email.localeCompare(b.email)
        case 'status':
          return rank[licenceState(a, now)] - rank[licenceState(b, now)] || a.expires_at - b.expires_at
        case 'expiry':
        default:
          return a.expires_at - b.expires_at
      }
    })
  }, [licences, query, sort, now])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="p-4 rounded-md bg-red-50 text-red-700">
        <p className="font-medium">GAUTO licences could not be loaded</p>
        <p className="mt-1 text-sm">{loadError}</p>
        <button
          onClick={() => load()}
          className="mt-3 px-3 py-1.5 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-500"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">GAUTO</h1>
          <p className="mt-1 text-sm text-gray-500">
            Licence administration
            {lastUpdated && <> · Updated {lastUpdated.toLocaleTimeString()}</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setInviteOpen((open) => !open)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white shadow-sm hover:bg-indigo-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Invite tester
          </button>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={cn('h-4 w-4 mr-2', refreshing && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      {/* The two facts that make this panel's buttons look "broken" if unlabelled. */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-1">
        <p>
          <strong className="font-semibold">Blocking stops renewals, not playback.</strong> Tokens live 30 days
          and are verified offline, so a blocked tester keeps watching until their current token lapses.
        </p>
        <p>
          <strong className="font-semibold">Resetting devices does not sign anyone out.</strong> It frees slots so
          a reinstalled phone can register again.
        </p>
      </div>

      {summary && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            label="Licences"
            value={summary.licences}
            sub={`${summary.active} active`}
            icon={KeyRound}
          />
          <StatCard
            label="Expiring in 30 days"
            value={summary.expiring_soon}
            sub={`${summary.expired} already expired`}
            icon={AlertTriangle}
            tone={summary.expiring_soon > 0 ? 'amber' : 'green'}
          />
          <StatCard
            label="Blocked"
            value={summary.revoked}
            sub="Stops renewing"
            icon={ShieldOff}
            tone={summary.revoked > 0 ? 'red' : 'green'}
          />
          <StatCard label="Devices in use" value={summary.devices} icon={Smartphone} tone="indigo" />
          <StatCard
            label="Slots free"
            value={licences.reduce((acc, row) => acc + Math.max(row.max_devices - row.devices, 0), 0)}
            sub="Across the licences listed below"
            icon={Users}
            tone="gray"
          />
        </div>
      )}

      {notice && <Notice tone="info">{notice}</Notice>}

      {inviteOpen && (
        <InviteForm
          busy={busyEmail !== null}
          error={actionError?.message ?? null}
          onCancel={() => {
            setInviteOpen(false)
            setActionError(null)
          }}
          onSubmit={invite}
        />
      )}

      <Card
        title={`Licences (${licences.length})`}
        subtitle="Click an email to open its devices, rename it, or remove it."
        action={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Filter by email or note"
                className="w-56 rounded-md border border-gray-300 pl-8 pr-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
              />
            </div>
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value as typeof sort)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
            >
              <option value="expiry">Sort: soonest expiry</option>
              <option value="status">Sort: needs attention</option>
              <option value="email">Sort: email</option>
              <option value="devices">Sort: devices used</option>
            </select>
          </div>
        }
      >
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Email', 'Expires', 'Days left', 'Status', 'Devices', 'Actions'].map((header) => (
                <th
                  key={header}
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {visible.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-400">
                  {licences.length === 0 ? 'No licences yet. Invite a tester to start.' : 'No licences match that filter.'}
                </td>
              </tr>
            ) : (
              visible.map((row) => {
                const state = licenceState(row, now)
                const remaining = daysUntil(row.expires_at, now)
                const slotsFull = row.devices >= row.max_devices
                const rowBusy = busyEmail === row.email

                return (
                  <tr key={row.email} className={cn('hover:bg-gray-50', rowBusy && 'opacity-60')}>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => openDetail(row.email)}
                        className="font-medium text-gray-900 hover:text-indigo-600 text-left"
                      >
                        {row.email}
                      </button>
                      {row.note && <div className="text-xs text-gray-400 mt-0.5">{row.note}</div>}
                      {actionError?.email === row.email && (
                        <div className="text-xs text-red-600 mt-1">{actionError.message}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(row.expires_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {state === 'revoked' ? (
                        <span className="text-gray-400">-</span>
                      ) : (
                        <span
                          className={cn(
                            'font-medium',
                            remaining <= 0 ? 'text-gray-500' : remaining < EXPIRING_SOON_DAYS ? 'text-amber-600' : 'text-gray-900',
                          )}
                        >
                          {remaining <= 0 ? `${Math.abs(remaining)}d ago` : `${remaining}d`}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <StatusBadge row={row} now={now} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={cn('font-medium', slotsFull ? 'text-red-600' : 'text-gray-900')}>
                        {row.devices} / {row.max_devices}
                      </span>
                      {slotsFull && <div className="text-xs text-red-500">slots full</div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-1">
                        <select
                          value=""
                          disabled={rowBusy}
                          onChange={(event) => {
                            const days = Number(event.target.value)
                            if (days) extend(row, days)
                          }}
                          className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none disabled:opacity-50"
                          title="Extend this licence"
                        >
                          <option value="">Extend…</option>
                          {EXTEND_OPTIONS.map((option) => (
                            <option key={option.days} value={option.days}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => openDetail(row.email)}
                          disabled={rowBusy}
                          title="Rename, devices, devices reset, remove"
                          className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => resetDevices(row.email)}
                          disabled={rowBusy || row.devices === 0}
                          title={
                            row.devices === 0
                              ? 'No devices to reset'
                              : 'Reset devices (frees slots; does not sign the phone out)'
                          }
                          className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30"
                        >
                          <Smartphone className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setBlocked(row, state !== 'revoked')}
                          disabled={rowBusy}
                          title={state === 'revoked' ? 'Unblock (renewals resume)' : 'Block (stops renewing)'}
                          className={cn(
                            'p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-50',
                            state === 'revoked' ? 'text-green-600 hover:text-green-700' : 'text-gray-400 hover:text-gray-700',
                          )}
                        >
                          {state === 'revoked' ? <ShieldCheck className="h-4 w-4" /> : <ShieldOff className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => confirmRemove(row)}
                          disabled={rowBusy}
                          title="Remove licence and its devices"
                          className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-gray-100 disabled:opacity-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </Card>

      <p className="text-xs text-gray-400">
        Every call is proxied through this dashboard's own server, which holds the admin secret. The secret is
        never sent to the browser.
      </p>

      {detailEmail && (
        <LicenceDrawer
          email={detailEmail}
          now={now}
          onClose={() => setDetailEmail(null)}
          onChanged={(message) => {
            setNotice(message)
            load(true)
          }}
          onError={(message) => setActionError({ email: detailEmail, message })}
          onRename={rename}
          onRemove={confirmRemove}
          onResetDevices={resetDevices}
          onRemoveDevice={removeDevice}
        />
      )}
    </div>
  )
}

/* ------------------------------------------------------------- invite form */

function InviteForm({
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  busy: boolean
  error: string | null
  onSubmit: (payload: GautoLicencePayload) => void
  onCancel: () => void
}) {
  const [email, setEmail] = useState('')
  const [amount, setAmount] = useState(365)
  const [unit, setUnit] = useState<ExpiryUnit>('days')
  const [maxDevices, setMaxDevices] = useState(1)
  const [note, setNote] = useState('')

  const valid = email.includes('@') && amount > 0

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        if (!valid) return
        onSubmit({
          email: email.trim(),
          expiresAt: addTerm(amount, unit),
          maxDevices: clampMaxDevices(maxDevices),
          note: note.trim() ? note.trim() : null,
        })
      }}
      className="bg-white shadow rounded-lg p-5 space-y-4"
    >
      <h3 className="text-base font-medium text-gray-900 flex items-center gap-2">
        <UserPlus className="h-4 w-4 text-gray-400" />
        Invite a tester
      </h3>
      <p className="text-xs text-gray-500">
        A licence is an invitation: the email decides who may hold it, the device is what it binds to. Inviting an
        existing email updates that licence.
      </p>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="sm:col-span-2">
          <Field label="Email">
            <input
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="tester@example.com"
              className={inputClass}
            />
          </Field>
        </div>
        <Field label="Term">
          <div className="flex gap-2">
            <input
              type="number"
              min={1}
              value={amount}
              onChange={(event) => setAmount(Number(event.target.value))}
              className={cn(inputClass, 'w-20')}
            />
            <select
              value={unit}
              onChange={(event) => setUnit(event.target.value as ExpiryUnit)}
              className={inputClass}
            >
              <option value="days">days</option>
              <option value="months">months</option>
              <option value="years">years</option>
            </select>
          </div>
        </Field>
        <Field label="Max devices">
          <input
            type="number"
            min={1}
            max={5}
            value={maxDevices}
            onChange={(event) => setMaxDevices(Number(event.target.value))}
            className={inputClass}
          />
        </Field>
        <div className="sm:col-span-2 lg:col-span-4">
          <Field label="Note (optional)">
            <input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="beta group A"
              className={inputClass}
            />
          </Field>
        </div>
      </div>

      {error && <Notice tone="error">{error}</Notice>}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={busy || !valid}
          className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
        >
          <Plus className="h-4 w-4 mr-2" />
          Invite
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

/* ------------------------------------------------------------ detail drawer */

function LicenceDrawer({
  email,
  now,
  onClose,
  onChanged,
  onError,
  onRename,
  onRemove,
  onResetDevices,
  onRemoveDevice,
}: {
  email: string
  now: number
  onClose: () => void
  onChanged: (message: string) => void
  onError: (message: string) => void
  onRename: (from: string, to: string) => Promise<void>
  onRemove: (licence: GautoLicenceListRow) => void | Promise<void>
  onResetDevices: (email: string) => Promise<void>
  onRemoveDevice: (deviceId: string, email: string) => Promise<void>
}) {
  const [detail, setDetail] = useState<GautoLicenceDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const [renameTo, setRenameTo] = useState(email)
  const [maxDevices, setMaxDevices] = useState(1)
  const [note, setNote] = useState('')
  const [amount, setAmount] = useState(0)
  const [unit, setUnit] = useState<ExpiryUnit>('months')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await gautoRequest<GautoLicenceDetail>(
        `${GAUTO_API.license}?email=${encodeURIComponent(email)}`,
      )
      setDetail(result)
      setMaxDevices(result.licence.max_devices)
      setNote(result.licence.note ?? '')
      setError(null)
    } catch (err) {
      if (err instanceof GautoApiError && err.status === 404) {
        onError('That licence no longer exists. Refresh the list.')
        onClose()
        return
      }
      setError(describeError(err))
    } finally {
      setLoading(false)
    }
  }, [email, onClose, onError])

  useEffect(() => {
    load()
  }, [load])

  const saveLicence = async (changes: LicenceWriteChanges, message: string) => {
    if (!detail) return
    setBusy(true)
    setError(null)
    try {
      await gautoRequest(GAUTO_API.license, {
        method: 'POST',
        body: buildLicenceWrite(detail.licence, changes),
      })
      await load()
      onChanged(message)
    } catch (err) {
      setError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  const licence = detail?.licence
  const devices = detail?.devices ?? []
  const state = licence ? licenceState(licence, now) : 'active'

  return (
    <Drawer
      onClose={onClose}
      title={
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 truncate">{email}</h2>
          {licence && (
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <StatusBadge row={licence} now={now} />
              <span className="text-xs text-gray-500">
                Expires {formatDate(licence.expires_at)}
                {state !== 'revoked' && ` · ${daysUntil(licence.expires_at, now)}d left`}
              </span>
            </div>
          )}
        </div>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        </div>
      ) : !licence ? (
        <Notice tone="error">{error ?? 'Licence unavailable.'}</Notice>
      ) : (
        <>
          {error && <Notice tone="error">{error}</Notice>}

          {/* Extend */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Extend</h3>
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-24">
                <Field label="Amount">
                  <input
                    type="number"
                    min={1}
                    value={amount || ''}
                    onChange={(event) => setAmount(Number(event.target.value))}
                    className={inputClass}
                    placeholder="12"
                  />
                </Field>
              </div>
              <div className="w-32">
                <Field label="Unit">
                  <select
                    value={unit}
                    onChange={(event) => setUnit(event.target.value as ExpiryUnit)}
                    className={inputClass}
                  >
                    <option value="days">days</option>
                    <option value="months">months</option>
                    <option value="years">years</option>
                  </select>
                </Field>
              </div>
              <button
                disabled={busy || amount <= 0}
                onClick={() =>
                  saveLicence(
                    { expiresAt: extendExpiry(licence.expires_at, amount, unit) },
                    `${email} extended by ${amount} ${unit}.`,
                  )
                }
                className="px-4 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
              >
                Extend
              </button>
            </div>
            <p className="text-xs text-gray-400">
              Adds to the later of today and the current expiry, so an expired licence gains the full term.
            </p>
          </section>

          {/* Access */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Access</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Max devices (1-5)">
                <div className="flex gap-2">
                  <input
                    type="number"
                    min={1}
                    max={5}
                    value={maxDevices}
                    onChange={(event) => setMaxDevices(Number(event.target.value))}
                    className={inputClass}
                  />
                  <button
                    disabled={busy || clampMaxDevices(maxDevices) === licence.max_devices}
                    onClick={() =>
                      saveLicence(
                        { maxDevices: clampMaxDevices(maxDevices) },
                        `Device limit for ${email} set to ${clampMaxDevices(maxDevices)}.`,
                      )
                    }
                    className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-40"
                  >
                    Save
                  </button>
                </div>
              </Field>
              <Field label="Note">
                <div className="flex gap-2">
                  <input
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    className={inputClass}
                    placeholder="beta group A"
                  />
                  <button
                    disabled={busy || note === (licence.note ?? '')}
                    onClick={() =>
                      saveLicence({ note: note.trim() ? note.trim() : null }, `Note updated for ${email}.`)
                    }
                    className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-40"
                  >
                    Save
                  </button>
                </div>
              </Field>
            </div>
          </section>

          {/* Devices */}
          <section className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-gray-900">
                Devices ({devices.length} / {licence.max_devices})
              </h3>
              <button
                disabled={busy || devices.length === 0}
                onClick={() => onResetDevices(email)}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-40"
              >
                <Smartphone className="h-3.5 w-3.5 mr-1.5" />
                Reset all devices
              </button>
            </div>

            {devices.length === 0 ? (
              <p className="text-sm text-gray-400">No devices registered yet.</p>
            ) : (
              <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md">
                {devices.map((device) => (
                  <li key={device.device_id} className="px-3 py-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <code className="text-xs text-gray-900 font-mono">
                          {shortDeviceId(device.device_id, 16)}
                        </code>
                        <button
                          title="Copy device id"
                          onClick={() => navigator.clipboard?.writeText(device.device_id)}
                          className="p-1 rounded text-gray-300 hover:text-gray-600"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        v{device.app_version ?? '?'} · {device.renewals} renewal
                        {device.renewals === 1 ? '' : 's'} · last seen {formatRelative(device.last_seen, now)}
                      </div>
                      <div className="text-xs text-gray-400">
                        First seen {formatDateTime(device.first_seen)}
                      </div>
                    </div>
                    <button
                      disabled={busy}
                      onClick={() => onRemoveDevice(device.device_id, email)}
                      title="Free this one slot"
                      className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-gray-100 disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-gray-400">
              A device id comes from the phone's hardware keystore, so it changes on reinstall - that is why a
              reinstalling tester consumes a slot.
            </p>
          </section>

          {/* Block / unblock */}
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-900">Access status</h3>
            {state === 'revoked' ? (
              <button
                disabled={busy}
                onClick={() => saveLicence({ status: 'active' }, `${email} unblocked.`)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-green-600 text-white shadow-sm hover:bg-green-500 disabled:opacity-50"
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                Unblock (renewals resume)
              </button>
            ) : (
              <button
                disabled={busy}
                onClick={() => saveLicence({ status: 'revoked' }, `${email} blocked.`)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-red-300 bg-white text-red-700 shadow-sm hover:bg-red-50 disabled:opacity-50"
              >
                <ShieldOff className="h-4 w-4 mr-2" />
                Block (stops renewing)
              </button>
            )}
            <p className="text-xs text-gray-400">
              Revoking stops renewals immediately. The app keeps working until the current 30-day token expires.
            </p>
          </section>

          {/* Rename */}
          <section className="space-y-2">
            <h3 className="text-sm font-semibold text-gray-900">Change email</h3>
            <div className="flex gap-2">
              <input
                type="email"
                value={renameTo}
                onChange={(event) => setRenameTo(event.target.value)}
                className={inputClass}
              />
              <button
                disabled={busy || !renameTo.includes('@') || renameTo.trim() === email}
                onClick={() => onRename(email, renameTo.trim())}
                className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-40 whitespace-nowrap"
              >
                Rename
              </button>
            </div>
            <p className="text-xs text-gray-400">
              Moves the licence and its devices together, keeping expiry, device limit and status.
            </p>
          </section>

          {/* Remove */}
          <section className="space-y-2 border-t border-gray-200 pt-5">
            <h3 className="text-sm font-semibold text-red-700">Danger zone</h3>
            <button
              disabled={busy}
              onClick={() => onRemove({ ...licence, devices: devices.length } as GautoLicenceListRow)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md bg-red-600 text-white shadow-sm hover:bg-red-500 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Remove licence and devices
            </button>
            <p className="text-xs text-gray-400">
              Irreversible. Devices are deleted first, so nothing is orphaned.
            </p>
          </section>
        </>
      )}
    </Drawer>
  )
}
